import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  MessageSquare,
  CheckCircle,
  AlertTriangle,
  Briefcase,
  Users,
  GraduationCap,
  DollarSign,
  Globe,
  Clock,
  ChevronDown,
  ChevronUp,
  Video,
  FileText,
  Star,
  Lightbulb
} from 'lucide-react';

const VisaInterviewPrep = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState('USA');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BookOpen },
    { id: 'questions', label: 'Common Questions', icon: MessageSquare },
    { id: 'tips', label: 'Tips & Best Practices', icon: Lightbulb },
    { id: 'checklist', label: 'Interview Checklist', icon: CheckCircle },
    { id: 'country-specific', label: 'Country-Specific', icon: Globe }
  ];

  const interviewQuestions = [
    {
      category: 'Study Plans & Academic Background',
      icon: GraduationCap,
      questions: [
        {
          question: 'Why do you want to study in [country]?',
          tips: [
            'Mention specific academic programs, research opportunities, or faculty expertise',
            'Discuss quality of education and international recognition',
            'Avoid saying "better job prospects" - focus on academic value',
            'Be genuine about your interest in the country\'s educational system'
          ],
          sampleAnswer: 'I\'ve chosen to study in the USA because of the cutting-edge research in Computer Science at [University Name]. The program\'s focus on artificial intelligence aligns perfectly with my undergraduate research, and I\'m particularly interested in working with Professor [Name] whose work on machine learning I\'ve followed closely.'
        },
        {
          question: 'Why did you choose this particular university/course?',
          tips: [
            'Research the university thoroughly beforehand',
            'Mention specific professors, research facilities, or course modules',
            'Explain how it aligns with your career goals',
            'Show you\'ve done your homework'
          ],
          sampleAnswer: 'I chose [University] for their renowned Engineering program, particularly their advanced robotics lab. The curriculum includes hands-on projects and industry partnerships that will give me practical experience. Additionally, the university\'s strong alumni network in my field will provide valuable connections.'
        },
        {
          question: 'What will you do after completing your studies?',
          tips: [
            'Express clear intention to return home',
            'Describe specific career opportunities in your home country',
            'Mention family ties and responsibilities',
            'Avoid being vague or uncertain'
          ],
          sampleAnswer: 'After completing my Master\'s degree, I plan to return to Nigeria to work in the renewable energy sector. I already have preliminary discussions with [Company Name] about joining their R&D team. Nigeria\'s growing focus on sustainable energy creates excellent opportunities for someone with my planned expertise.'
        },
        {
          question: 'What did you study previously? How is this course related?',
          tips: [
            'Clearly explain the logical progression',
            'If it\'s a different field, explain your motivation for change',
            'Highlight relevant skills or knowledge from previous studies',
            'Show how both degrees complement each other'
          ],
          sampleAnswer: 'I completed my Bachelor\'s in Mechanical Engineering, and this Master\'s in Renewable Energy Engineering is a natural progression. My undergraduate capstone project on solar panel efficiency sparked my interest in sustainable energy, and this program will allow me to specialize in that area.'
        }
      ]
    },
    {
      category: 'Financial Capacity',
      icon: DollarSign,
      questions: [
        {
          question: 'How will you finance your education?',
          tips: [
            'Be specific about funding sources',
            'Have exact figures ready',
            'Mention scholarships, family support, savings, or loans',
            'Show financial documentation if asked'
          ],
          sampleAnswer: 'I\'ve been awarded a partial scholarship of $15,000 per year from the university. My parents will support me with $20,000 annually from their savings and business income. I also have $10,000 in personal savings from my previous job. The total covers tuition and living expenses for the two-year program.'
        },
        {
          question: 'What do your parents do? What is their annual income?',
          tips: [
            'State their occupation clearly',
            'Be honest about income levels',
            'Have supporting documents ready',
            'Explain if multiple sources of income exist'
          ],
          sampleAnswer: 'My father owns a manufacturing business in Lagos, and my mother is a senior accountant at [Company]. Their combined annual income is approximately ₦25 million (about $35,000). They\'ve been saving for my education for the past five years.'
        },
        {
          question: 'Do you have any sponsors? Who are they?',
          tips: [
            'Clearly identify sponsors and their relationship to you',
            'Explain why they\'re willing to sponsor you',
            'Have sponsor affidavits and financial documents ready',
            'Be prepared to answer questions about sponsor\'s finances'
          ],
          sampleAnswer: 'My parents are my primary sponsors. Additionally, my uncle who lives in [city] has offered to contribute $5,000 toward my living expenses. He\'s a software engineer and wants to support my education in the same field.'
        },
        {
          question: 'Have you applied for any scholarships?',
          tips: [
            'List all scholarships applied for and received',
            'Mention amounts and what they cover',
            'If not awarded yet, mention pending applications',
            'Show you\'ve actively sought funding opportunities'
          ],
          sampleAnswer: 'Yes, I\'ve received a merit-based scholarship from the university covering 40% of tuition. I\'ve also applied for the [Scholarship Name] which announces results in June, and I\'m a finalist for the [Foundation] grant which could provide an additional $10,000.'
        }
      ]
    },
    {
      category: 'Immigration Intent & Ties to Home Country',
      icon: Globe,
      questions: [
        {
          question: 'Do you have any relatives in [country]?',
          tips: [
            'Be honest - lying can result in visa denial',
            'If yes, explain your relationship but emphasize your study purpose',
            'If no, simply state that clearly',
            'Don\'t be nervous about this question'
          ],
          sampleAnswer: 'Yes, my cousin lives in California, but I haven\'t seen her in years. My decision to study in the USA is purely based on academic opportunities, not family connections. I\'ve chosen [University in different state] because of their program quality.'
        },
        {
          question: 'What ties do you have to your home country?',
          tips: [
            'Mention family responsibilities',
            'Discuss property or business ownership',
            'Highlight career opportunities waiting for you',
            'Show strong community or professional connections'
          ],
          sampleAnswer: 'I have strong ties to Nigeria. My immediate family lives here, and I\'m the eldest of three siblings. My parents are aging and I\'ll need to support them. Additionally, I\'m a part-owner of our family business, which I plan to expand using my new skills. I also have a long-term relationship with my partner who is building her career here.'
        },
        {
          question: 'Why not study in your home country?',
          tips: [
            'Focus on specific academic advantages',
            'Mention specialized courses or research facilities not available at home',
            'Avoid disparaging your home country\'s education system',
            'Be respectful and factual'
          ],
          sampleAnswer: 'While Nigeria has good universities, this particular specialization in renewable energy systems is not widely available here. The hands-on experience with cutting-edge solar technology and access to industry partnerships in the USA will give me unique expertise to bring back home.'
        },
        {
          question: 'Do you plan to work while studying?',
          tips: [
            'Know the legal working restrictions for students',
            'If planning to work, mention on-campus options only',
            'Emphasize it\'s for experience, not necessity',
            'Show you can afford education without working'
          ],
          sampleAnswer: 'I understand that F-1 students can work up to 20 hours per week on campus. If there\'s an opportunity for a research assistantship in my department, I\'d be interested for the practical experience, but it\'s not necessary as my education is fully funded.'
        }
      ]
    },
    {
      category: 'Personal Background',
      icon: Users,
      questions: [
        {
          question: 'Tell me about yourself.',
          tips: [
            'Keep it concise - 1-2 minutes',
            'Focus on academic background and career goals',
            'Mention relevant achievements',
            'Connect it to your study plans'
          ],
          sampleAnswer: 'I\'m a Mechanical Engineering graduate from [University] with three years of work experience in the manufacturing sector. I\'ve always been passionate about sustainable technology, and I\'ve led several energy efficiency projects at my company. I\'m now ready to deepen my expertise through a Master\'s program to pursue my goal of developing renewable energy solutions for Nigeria.'
        },
        {
          question: 'Have you traveled abroad before?',
          tips: [
            'Be honest about previous travel',
            'If yes, mention countries and purpose',
            'If you returned on time, emphasize that',
            'If no previous travel, that\'s perfectly fine'
          ],
          sampleAnswer: 'Yes, I\'ve traveled to Ghana for a regional conference in 2022 and to South Africa for a family wedding in 2023. Both were short trips and I returned as scheduled. This will be my first time traveling to [country].'
        },
        {
          question: 'Are you married? Do you have children?',
          tips: [
            'Answer honestly',
            'If married, explain spouse\'s situation (job, family)',
            'If children, explain childcare arrangements',
            'Show family supports your study plans'
          ],
          sampleAnswer: 'I\'m engaged but not yet married. My fiancée is a teacher in Lagos and fully supports my study plans. We plan to marry after I complete my degree and return home.'
        },
        {
          question: 'What will you do if your visa is denied?',
          tips: [
            'Stay calm and positive',
            'Express that you hope it won\'t happen',
            'Mention you\'d reapply or explore alternatives',
            'Don\'t sound desperate or threatening'
          ],
          sampleAnswer: 'I\'m confident in my application and hopeful for approval. However, if denied, I would try to understand the reasons, address any concerns, and reapply. Education is important to me, and I\'m committed to pursuing this goal through proper channels.'
        }
      ]
    }
  ];

  const dosAndDonts = {
    dos: [
      {
        title: 'Arrive Early',
        description: 'Arrive at least 30 minutes before your scheduled time. This shows respect and gives you time to compose yourself.'
      },
      {
        title: 'Dress Professionally',
        description: 'Wear formal business attire (suit or professional dress). First impressions matter significantly.'
      },
      {
        title: 'Be Honest',
        description: 'Always tell the truth. Inconsistencies can lead to immediate visa denial and future complications.'
      },
      {
        title: 'Be Confident and Clear',
        description: 'Speak clearly, make eye contact, and answer confidently. If you don\'t understand a question, politely ask for clarification.'
      },
      {
        title: 'Keep Answers Concise',
        description: 'Answer directly and briefly. Don\'t ramble or volunteer unnecessary information.'
      },
      {
        title: 'Bring All Documents',
        description: 'Organize all required documents in a folder. Even if not asked, having them shows preparedness.'
      },
      {
        title: 'Show Enthusiasm',
        description: 'Demonstrate genuine excitement about your study plans and future goals.'
      },
      {
        title: 'Maintain Positive Body Language',
        description: 'Smile, maintain good posture, and avoid fidgeting. Non-verbal communication is important.'
      }
    ],
    donts: [
      {
        title: 'Don\'t Memorize Answers',
        description: 'Avoid sounding rehearsed. Visa officers can tell when you\'re reciting memorized scripts.'
      },
      {
        title: 'Don\'t Argue',
        description: 'If the officer seems skeptical, remain calm and provide clear answers. Never argue or get defensive.'
      },
      {
        title: 'Don\'t Show Immigration Intent',
        description: 'Avoid statements that suggest you want to stay permanently. Always emphasize plans to return home.'
      },
      {
        title: 'Don\'t Bring Family Members',
        description: 'Attend the interview alone unless you\'re applying as a family. Having parents answer for you looks bad.'
      },
      {
        title: 'Don\'t Use Your Phone',
        description: 'Turn off your phone completely. Don\'t check it while waiting or during the interview.'
      },
      {
        title: 'Don\'t Speak Negatively',
        description: 'Avoid criticizing your home country, previous schools, or anyone. Stay positive.'
      },
      {
        title: 'Don\'t Provide False Information',
        description: 'Never submit fake documents or lie about your background. This can result in a permanent ban.'
      },
      {
        title: 'Don\'t Dress Casually',
        description: 'Avoid jeans, t-shirts, sneakers, or overly casual attire. This is a formal professional interview.'
      }
    ]
  };

  const interviewChecklist = [
    {
      category: 'Documents to Bring',
      items: [
        'Valid passport (original)',
        'DS-160 confirmation page (USA) / Application confirmation',
        'Visa appointment confirmation',
        'University acceptance letter (I-20 for USA, CAS for UK)',
        'Academic transcripts and certificates',
        'Standardized test scores (TOEFL, IELTS, GRE, GMAT)',
        'Financial documents (bank statements, sponsor letters, scholarship awards)',
        'Proof of ties to home country (property deeds, employment letters)',
        'Resume/CV',
        'Study plan or statement of purpose'
      ]
    },
    {
      category: 'Day Before Interview',
      items: [
        'Review all documents and ensure they\'re organized',
        'Practice common interview questions',
        'Prepare outfit (business formal)',
        'Check embassy location and route',
        'Get adequate sleep',
        'Review university program details',
        'Know exact tuition fees and living costs'
      ]
    },
    {
      category: 'Day of Interview',
      items: [
        'Eat a light breakfast',
        'Arrive 30 minutes early',
        'Turn off mobile phone',
        'Bring only necessary items (no large bags)',
        'Stay calm and confident',
        'Be respectful to all staff',
        'Answer questions clearly and honestly'
      ]
    },
    {
      category: 'After Interview',
      items: [
        'Note down any additional documents requested',
        'Submit additional documents promptly if asked',
        'Track visa application status online',
        'Wait for official notification',
        'Don\'t make travel plans until visa is approved'
      ]
    }
  ];

  const countrySpecificTips = {
    USA: [
      {
        title: 'F-1 Visa Specifics',
        content: 'Demonstrate strong ties to home country and clear intent to return after studies. The officer assumes you want to immigrate unless proven otherwise.'
      },
      {
        title: 'SEVIS Fee',
        content: 'Ensure you\'ve paid the SEVIS I-901 fee at least 3 days before the interview and bring the receipt.'
      },
      {
        title: 'Interview Duration',
        content: 'USA interviews are typically very short (2-5 minutes). Be concise and direct with answers.'
      },
      {
        title: 'Common Rejection Reasons',
        content: 'Section 214(b) - failure to prove non-immigrant intent. If denied, understand why before reapplying.'
      },
      {
        title: 'Supporting Documents',
        content: 'Bring evidence of family ties, property ownership, or job prospects in home country.'
      }
    ],
    UK: [
      {
        title: 'Credibility Interview',
        content: 'Focus on genuine student intent. Be prepared for detailed questions about your course and university.'
      },
      {
        title: 'CAS Statement',
        content: 'Know your CAS number and details. The officer may ask specific questions about information on it.'
      },
      {
        title: 'Financial Requirements',
        content: 'Must show funds for tuition + £1,334/month living costs (outside London) or £1,023/month (in London).'
      },
      {
        title: 'Tuberculosis Test',
        content: 'Ensure you\'ve completed TB test at an approved clinic if you\'re from a listed country.'
      },
      {
        title: 'English Proficiency',
        content: 'The interview may be conducted in English to assess your language skills.'
      }
    ],
    Canada: [
      {
        title: 'Study Permit vs. Visa',
        content: 'Understand the difference between a study permit and a visa (eTA). You may need both.'
      },
      {
        title: 'Provincial Nomination',
        content: 'If applicable, mention provincial nominee program awareness but emphasize study focus.'
      },
      {
        title: 'Post-Graduation Work Permit',
        content: 'You can mention PGWP awareness but emphasize it\'s optional and you plan to return home.'
      },
      {
        title: 'Biometrics',
        content: 'Ensure biometrics are completed before the interview or as requested.'
      },
      {
        title: 'Financial Proof',
        content: 'Show CAD $10,000 + tuition for first year, or CAD $11,000 if studying in Quebec.'
      }
    ],
    Australia: [
      {
        title: 'Genuine Temporary Entrant (GTE)',
        content: 'Primary focus is proving you\'re a genuine temporary student with intent to return home.'
      },
      {
        title: 'Health Insurance',
        content: 'Must have Overseas Student Health Cover (OSHC) for the entire duration of stay.'
      },
      {
        title: 'English Requirements',
        content: 'Bring evidence of IELTS/PTE scores. Minimum requirements vary by institution.'
      },
      {
        title: 'Financial Capacity',
        content: 'Show AUD $21,041/year for living costs + tuition + return airfare.'
      },
      {
        title: 'Course Relevance',
        content: 'Clearly explain how your chosen course relates to previous studies or career goals.'
      }
    ]
  };

  const toggleQuestion = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setExpandedQuestion(expandedQuestion === key ? null : key);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-4">
            <Video className="h-10 w-10 mr-3" />
            <h1 className="text-4xl font-bold">Visa Interview Preparation</h1>
          </div>
          <p className="text-lg text-indigo-100 max-w-3xl">
            Comprehensive guide to help you prepare for your student visa interview.
            Practice common questions, learn best practices, and increase your chances of success.
          </p>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap
                    ${activeTab === tab.id
                      ? 'border-indigo-600 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Welcome to Interview Prep</h2>
              <p className="text-gray-700 mb-4">
                The visa interview is a crucial step in your study abroad journey. This comprehensive guide will help you:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Understand common interview questions and how to answer them effectively</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Learn best practices and what visa officers look for</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Get country-specific tips for USA, UK, Canada, and Australia</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Use our checklist to ensure you're fully prepared</span>
                </li>
              </ul>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
                <Clock className="h-8 w-8 text-blue-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Start Early</h3>
                <p className="text-gray-700">
                  Begin your preparation at least 2-3 weeks before your interview date. Practice answering questions out loud and get comfortable with your story.
                </p>
              </div>

              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
                <Briefcase className="h-8 w-8 text-green-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Be Professional</h3>
                <p className="text-gray-700">
                  Treat the interview like a job interview. Dress formally, arrive early, bring all documents, and maintain professional demeanor throughout.
                </p>
              </div>

              <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6">
                <FileText className="h-8 w-8 text-purple-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Know Your Documents</h3>
                <p className="text-gray-700">
                  Review all your application documents thoroughly. Be prepared to answer questions about any information you've provided.
                </p>
              </div>

              <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-6">
                <Star className="h-8 w-8 text-orange-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Be Yourself</h3>
                <p className="text-gray-700">
                  Don't memorize scripts. Be genuine, honest, and confident. Visa officers can spot rehearsed answers and it raises suspicion.
                </p>
              </div>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
              <div className="flex items-start">
                <AlertTriangle className="h-6 w-6 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Important Reminder</h3>
                  <p className="text-gray-700">
                    The purpose of the interview is to verify that you are a genuine student with no intent to immigrate.
                    Your goal is to demonstrate strong ties to your home country and clear academic/career objectives.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Common Questions Tab */}
        {activeTab === 'questions' && (
          <div className="space-y-6">
            {interviewQuestions.map((category, categoryIndex) => {
              const CategoryIcon = category.icon;
              return (
                <div key={categoryIndex} className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center mb-4">
                    <CategoryIcon className="h-6 w-6 text-indigo-600 mr-3" />
                    <h2 className="text-2xl font-bold text-gray-900">{category.category}</h2>
                  </div>

                  <div className="space-y-4">
                    {category.questions.map((q, questionIndex) => {
                      const isExpanded = expandedQuestion === `${categoryIndex}-${questionIndex}`;
                      return (
                        <div key={questionIndex} className="border border-gray-200 rounded-lg">
                          <button
                            onClick={() => toggleQuestion(categoryIndex, questionIndex)}
                            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50"
                          >
                            <span className="font-semibold text-gray-900">{q.question}</span>
                            {isExpanded ? (
                              <ChevronUp className="h-5 w-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-gray-400" />
                            )}
                          </button>

                          {isExpanded && (
                            <div className="p-4 bg-gray-50 border-t border-gray-200">
                              <div className="mb-4">
                                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                                  <Lightbulb className="h-5 w-5 text-yellow-500 mr-2" />
                                  Tips for Answering:
                                </h4>
                                <ul className="list-disc list-inside space-y-1 text-gray-700">
                                  {q.tips.map((tip, tipIndex) => (
                                    <li key={tipIndex} className="ml-4">{tip}</li>
                                  ))}
                                </ul>
                              </div>

                              <div className="bg-white p-4 rounded-lg border border-indigo-200">
                                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                                  <MessageSquare className="h-5 w-5 text-indigo-600 mr-2" />
                                  Sample Answer:
                                </h4>
                                <p className="text-gray-700 italic">"{q.sampleAnswer}"</p>
                                <p className="text-sm text-gray-500 mt-2">
                                  Note: This is a sample answer for guidance. Personalize your response based on your actual situation.
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Tips & Best Practices Tab */}
        {activeTab === 'tips' && (
          <div className="space-y-6">
            {/* Do's */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center mb-6">
                <CheckCircle className="h-8 w-8 text-green-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">Do's - What You Should Do</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {dosAndDonts.dos.map((item, index) => (
                  <div key={index} className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                      {item.title}
                    </h3>
                    <p className="text-gray-700 text-sm">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Don'ts */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center mb-6">
                <AlertTriangle className="h-8 w-8 text-red-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">Don'ts - What You Should Avoid</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {dosAndDonts.donts.map((item, index) => (
                  <div key={index} className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                      <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
                      {item.title}
                    </h3>
                    <p className="text-gray-700 text-sm">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Tips */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Mock Interview Practice</h3>
              <p className="text-gray-700 mb-4">
                Practice makes perfect. Here's how to prepare effectively:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-indigo-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Practice with a friend or family member acting as the visa officer</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-indigo-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Record yourself answering questions and review your body language</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-indigo-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Time your answers - keep them under 1-2 minutes</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-indigo-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Practice in formal attire to get comfortable</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-indigo-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Get feedback on your confidence and clarity</span>
                </li>
              </ul>
            </div>
          </div>
        )}

        {/* Checklist Tab */}
        {activeTab === 'checklist' && (
          <div className="space-y-6">
            {interviewChecklist.map((section, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{section.category}</h2>
                <div className="space-y-2">
                  {section.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-start">
                      <div className="flex-shrink-0 mt-1">
                        <div className="h-5 w-5 border-2 border-gray-300 rounded"></div>
                      </div>
                      <span className="ml-3 text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="bg-indigo-50 border-2 border-indigo-200 rounded-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Print This Checklist</h3>
              <p className="text-gray-700 mb-4">
                We recommend printing this checklist and checking off items as you complete them.
                This ensures you don't forget any important preparation steps.
              </p>
              <button className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                Download PDF Checklist
              </button>
            </div>
          </div>
        )}

        {/* Country-Specific Tab */}
        {activeTab === 'country-specific' && (
          <div className="space-y-6">
            {/* Country Selector */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Select Your Destination Country</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {['USA', 'UK', 'Canada', 'Australia'].map((country) => (
                  <button
                    key={country}
                    onClick={() => setSelectedCountry(country)}
                    className={`
                      p-4 rounded-lg border-2 font-semibold text-lg transition-colors
                      ${selectedCountry === country
                        ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-indigo-300'
                      }
                    `}
                  >
                    {country}
                  </button>
                ))}
              </div>
            </div>

            {/* Country-Specific Tips */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center mb-6">
                <Globe className="h-8 w-8 text-indigo-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">{selectedCountry}-Specific Interview Tips</h2>
              </div>

              <div className="space-y-4">
                {countrySpecificTips[selectedCountry].map((tip, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h3 className="font-semibold text-gray-900 mb-2">{tip.title}</h3>
                    <p className="text-gray-700">{tip.content}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
              <p className="text-gray-700">
                <strong>Note:</strong> These are general tips based on common experiences.
                Always check the official embassy website for the most current requirements and procedures.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="bg-white border-t border-gray-200 py-6 mt-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <button
              onClick={() => navigate('/services/visa')}
              className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Back to Visa Services
            </button>

            <button
              onClick={() => window.print()}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center"
            >
              <FileText className="h-5 w-5 mr-2" />
              Print This Guide
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisaInterviewPrep;
